import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Card } from "@/components/ui/card";
import { useNotifications } from "@/context/NotificationsContext";
import { healthOf } from "@/lib/health";
import { cn } from "@/lib/utils";

// "Needs attention": one compact list, worst first. Each row = a section with how many items
// are critical (act now) / stuck (not moving) and how many are new since you last looked.
// Sections with nothing to do collapse into one "All moving" line.
const SECTIONS = [
  { key: "requests", label: "Requests & Leads", icon: "Send", path: "/requests", colls: ["requests"] },
  { key: "projects", label: "Projects & milestones", icon: "FolderKanban", path: "/projects", colls: ["projects", "milestone_plans", "project_milestones"] },
  { key: "visits", label: "Site visits", icon: "ClipboardCheck", path: "/visits", colls: ["supervisor_visits"] },
  { key: "promise", label: "Brugo Promise", icon: "ShieldCheck", path: "/promise", colls: ["promise_requests"] },
  { key: "care", label: "Customer Care", icon: "Headset", path: "/care", colls: ["support_conversations", "support_tickets", "critical_notifications"] },
  { key: "orders", label: "Orders", icon: "Package", path: "/orders", colls: ["orders"] },
  { key: "professionals", label: "Pro approvals", icon: "UserCheck", path: "/professionals?tab=approval", colls: ["professionals"] },
  { key: "payments", label: "Payments", icon: "CreditCard", path: "/payments", colls: ["razorpay_payments"] },
];

export function AttentionBoard() {
  const { data = {}, unseenBySection } = useNotifications();
  const nav = useNavigate();
  const stats = useMemo(() => SECTIONS.map((s) => {
    let critical = 0, stuck = 0;
    s.colls.forEach((c) => (data[c] || []).forEach((d) => {
      const h = healthOf(c, d);
      if (h?.level === "critical") critical += 1; else if (h?.level === "stuck") stuck += 1;
    }));
    return { ...s, critical, stuck, unseen: unseenBySection[s.key] || 0 };
  }).sort((a, b) => b.critical - a.critical || b.stuck - a.stuck || b.unseen - a.unseen), [data, unseenBySection]);
  const busy = stats.filter((s) => s.critical || s.stuck || s.unseen);
  const calm = stats.filter((s) => !s.critical && !s.stuck && !s.unseen);

  return (
    <Card className="mb-6 overflow-hidden" data-testid="attention-board">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <h2 className="font-heading font-semibold text-foreground">Needs attention</h2>
        <span className="ml-auto flex items-center gap-3 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1"><Icons.AlertTriangle className="h-3 w-3 text-red-600" />critical — act now</span>
          <span className="flex items-center gap-1"><Icons.Hourglass className="h-3 w-3 text-amber-600" />stuck — not moving</span>
          <span className="flex items-center gap-1"><span className="cc-new-dot" />new</span>
        </span>
      </div>
      <div className="divide-y divide-border">
        {busy.map((s) => {
          const Icon = Icons[s.icon] || Icons.Circle;
          return (
            <button key={s.key} onClick={() => nav(s.path)} data-testid={`attention-${s.key}`}
              className={cn("w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-accent/40 transition-colors", s.critical ? "cc-critical" : s.stuck ? "cc-stuck" : "")}>
              <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-sm font-medium text-foreground w-48 shrink-0 truncate">{s.label}</span>
              <span className="flex items-center gap-4 text-sm tabular-nums">
                {s.critical > 0 && <span className="flex items-center gap-1 text-red-700"><Icons.AlertTriangle className="h-3.5 w-3.5" /><b>{s.critical}</b><span className="text-xs text-red-700/80">critical</span></span>}
                {s.stuck > 0 && <span className="flex items-center gap-1 text-amber-700"><Icons.Hourglass className="h-3.5 w-3.5" /><b>{s.stuck}</b><span className="text-xs text-amber-700/80">stuck</span></span>}
                {s.unseen > 0 && <span className="flex items-center gap-1.5 text-muted-foreground"><span className="cc-new-dot" /><b className="text-foreground">{s.unseen > 99 ? "99+" : s.unseen}</b><span className="text-xs">new</span></span>}
              </span>
              <Icons.ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
            </button>
          );
        })}
        {calm.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-xs text-muted-foreground" data-testid="attention-calm">
            <span className="flex items-center gap-1 text-emerald-700"><Icons.CheckCircle2 className="h-3.5 w-3.5" />All moving:</span>
            {calm.map((s) => <button key={s.key} onClick={() => nav(s.path)} className="hover:text-foreground hover:underline">{s.label}</button>)}
          </div>
        )}
      </div>
    </Card>
  );
}
